import React, { useEffect, useState } from 'react';
import './RecentVideo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const RecentVideo = ({ channelId, title }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoId, setVideoId] = useState(null);

  useEffect(() => {
    if (!channelId) return; // Safety check
    setIsVisible(true);

    const fetchLatestVideo = async () => {
      // Create a cache key based on channel ID
      const cacheKey = `youtube_video_${channelId}`;
      const cachedData = localStorage.getItem(cacheKey);

      // Check if cached data exists and is still valid
      if (cachedData) {
        try {
          const { videoId: cachedVideoId, expiresAt } = JSON.parse(cachedData);
          const now = new Date();
          const expiry = new Date(expiresAt);
          
          // If cache is still valid, use it
          if (now < expiry) {
            console.log(`Using cached video for channel ${channelId}`);
            setVideoId(cachedVideoId);
            return;
          }
        } catch (err) {
          console.error('Error parsing cached data:', err);
        }
      }

      // If no valid cache, fetch from backend
      try {
        console.log(`Fetching video for channel ${channelId} from backend`);
        const res = await fetch(`${BACKEND_URL}/api/latest-video/${channelId}`);
        const data = await res.json();
        
        if (data.videoId) {
          // Cache the video ID with expiration time
          localStorage.setItem(cacheKey, JSON.stringify({
            videoId: data.videoId,
            expiresAt: data.expiresAt
          }));
          setVideoId(data.videoId);
          
          if (data.cached) {
            console.log(`Backend returned cached video for channel ${channelId}`);
          } else if (data.stale) {
            console.warn(`Backend returned stale cache for channel ${channelId}: ${data.error}`);
          } else {
            console.log(`Backend fetched fresh video for channel ${channelId}`);
          }
        } else if (data.error) {
          console.error(`Error from backend: ${data.error}`);
          
          // If backend fails but we have old cache, use it anyway as fallback
          if (cachedData) {
            try {
              const { videoId: cachedVideoId } = JSON.parse(cachedData);
              console.log(`Using stale cache as fallback for channel ${channelId}`);
              setVideoId(cachedVideoId);
            } catch (fallbackErr) {
              console.error('Fallback cache also failed:', fallbackErr);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch latest video from backend:', err);
        
        // If fetch fails but we have old cache, use it anyway as fallback
        if (cachedData) {
          try {
            const { videoId: cachedVideoId } = JSON.parse(cachedData);
            console.log(`Using stale cache as fallback for channel ${channelId}`);
            setVideoId(cachedVideoId);
          } catch (fallbackErr) {
            console.error('Fallback cache also failed:', fallbackErr);
          }
        }
      }
    };

    fetchLatestVideo();
  }, [channelId]);

  // Only render if we have a valid videoId
  if (!videoId) return null;

  return (
    <div className={`recent-video ${isVisible ? 'fade-in-up' : ''}`}>
      <h2 className="section-title">{title}</h2>
      <div className="video-container">
        <iframe
          width="100%"
          height="500"
          src={videoId ? `https://www.youtube.com/embed/${videoId}` : null}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </div>
  );
};

export default RecentVideo;
